// Decode a named transport row into a fully enriched Site.

import { ASSET, colorBy, PHASE } from './constants';
import { fmtTime } from './format';
import { hexa } from './geo';
import { ATTENTION_CONF, calc, suit } from './solar';
import type { AssetKey, Site, SiteRow } from './types';

export function enrich(r: SiteRow, i: number): Site {
  const {
    id: vid,
    site_name: name,
    location_name: sub,
    has_solar: hasSolar,
    has_cabinet: hasCabinet,
    phase,
    roof_area_sqm: area,
    confidence_pct: confidence,
    lat,
    lng,
    last_update: updated,
    bbox,
    status,
    display_height_m: displayHeightM,
    estimated_stories: estimatedStories,
    display_height_year: heightYear,
    display_height_source: heightSource,
    display_height_quality: heightQuality,
    display_height_method: heightMethod,
    display_height_valid_pixel_count: heightValidPixelCount,
    auto_open_building_id: autoOpenBuildingId,
    display_open_building_id: displayOpenBuildingId,
    geometry_source: geometrySource,
    geometry_source_version: geometrySourceVersion,
    gers_id: gersId,
    display_pvout_kwh_kwp_year: pvoutKwhKwpYear,
    display_ghi_kwh_m2_year: ghiKwhM2Year,
    display_solar_source: solarSource,
    display_solar_source_version: solarSourceVersion,
  } = r;
  const measuredPvout =
    typeof pvoutKwhKwpYear === 'number' && Number.isFinite(pvoutKwhKwpYear) && pvoutKwhKwpYear > 0
      ? pvoutKwhKwpYear
      : undefined;
  const measuredGhi =
    typeof ghiKwhM2Year === 'number' && Number.isFinite(ghiKwhM2Year) && ghiKwhM2Year > 0
      ? ghiKwhM2Year
      : null;
  const pvoutPresent = measuredPvout !== undefined;
  const m = calc(area, { yieldPerKwp: measuredPvout });
  // A site has no usable roof when there's no auto-detected footprint and no
  // measured area. The backend stamps status='no_roof' once detection has run
  // and found nothing — prefer that authoritative signal, fall back to inferring.
  const noRoof = status === 'no_roof' || (!bbox?.length && !(area > 0));
  const assets = hasSolar && hasCabinet ? 'both' : hasSolar ? 'solar' : 'cabinet';
  const assetList: AssetKey[] = hasSolar && hasCabinet ? ['solar', 'cabinet'] : [hasSolar ? 'solar' : 'cabinet'];
  const primaryAsset = hasSolar ? ASSET.solar : ASSET.cabinet;
  const ph = PHASE[phase];
  const su = suit(m.kwp, confidence);
  const shape = hasSolar && hasCabinet ? 'both' : primaryAsset.shape;
  const assetShort = hasSolar && hasCabinet ? 'SOLAR + CABINET' : primaryAsset.short;
  const assetLabel = hasSolar && hasCabinet ? 'Solar + Cabinet' : primaryAsset.label;
  let primary = primaryAsset.color;
  if (colorBy() === 'phase') primary = ph.color;
  else if (colorBy() === 'suitability') primary = su.color;

  return {
    id: `S${i}`,
    vid,
    name,
    sub,
    assets,
    assetList,
    hasSolar,
    hasCabinet,
    phase,
    area,
    confidence,
    lat,
    lng,
    updated,
    auto_bbox: bbox,
    status,
    displayHeightM,
    estimatedStories,
    provenance: {
      autoOpenBuildingId,
      displayOpenBuildingId,
      geometrySource,
      geometrySourceVersion,
      gersId,
      heightYear,
      heightSource,
      heightQuality,
      heightMethod,
      heightValidPixelCount,
      pvoutKwhKwpYear: measuredPvout ?? null,
      ghiKwhM2Year: measuredGhi,
      pvoutPresent,
      solarSource: solarSource ?? null,
      solarSourceVersion: solarSourceVersion ?? null,
      modelVersion: m.modelVersion,
    },
    noRoof,
    pvoutPresent,
    typeColor: primaryAsset.color,
    typeShort: assetShort,
    assetLabel,
    shape,
    primary,
    ring: ph.color,
    phaseColor: ph.color,
    phaseLabel: ph.label,
    phaseShort: ph.short,
    suitColor: su.color,
    suitLabel: su.label,
    suitBg: su.bg,
    typeBg: hexa(primaryAsset.color, 0.12),
    phaseBg: hexa(ph.color, 0.12),
    mwh: (m.annual / 1000).toFixed(1),
    areaShort: Math.round(area).toLocaleString(),
    confStr: `${confidence}%`,
    coordStr: `${lat.toFixed(4)}, ${lng.toFixed(4)}`,
    updatedStr: `Updated ${fmtTime(updated) || '—'}`,
    ...m,
  };
}

export function needsAttention(s: Pick<Site, 'noRoof' | 'confidence'>): boolean {
  return s.noRoof || (s.confidence != null && s.confidence < ATTENTION_CONF);
}
